import React, { useRef, useEffect } from 'react';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { MdRefresh } from 'react-icons/md';
import { Button } from '../../../../ui/button';

const FilterPanel = ({
  filterTitle,
  setFilterTitle,
  filterMediaType,
  setFilterMediaType,
  filterTimeUploaded,
  setFilterTimeUploaded,
  resetFilters,
  applyFilters,
  isInModal = false
}) => {
  // Use a form ref to prevent unintended form submissions
  const formRef = useRef(null);

  // Prevent form submission on mount and cleanup
  useEffect(() => {
    const form = formRef.current;
    
    if (form) {
      const preventSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      };
      
      // Add novalidate and autocomplete attributes to the form
      form.setAttribute('novalidate', 'novalidate');
      form.setAttribute('autocomplete', 'off');
      
      // Prevent form submission
      form.addEventListener('submit', preventSubmit, true);
      
      // Prevent Enter key from submitting the form
      const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          applyFilters(e);
          return false;
        }
      };
      
      form.addEventListener('keydown', handleKeyDown, true);
      
      // Prevent events from bubbling when inside a modal
      if (isInModal) {
        document.addEventListener('submit', preventSubmit, true);
      }
    
      return () => {
        if (form) {
          form.removeEventListener('submit', preventSubmit, true);
          form.removeEventListener('keydown', handleKeyDown, true);
          
          if (isInModal) {
            document.removeEventListener('submit', preventSubmit, true);
          }
        }
      };
    }
  }, [applyFilters, isInModal]);

  // Handle Enter key press
  const handleEnterKey = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      applyFilters(e);
    }
  };

  // Handle search button click
  const handleSearchClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    applyFilters(e);
  };

  // Use consistent relative positioning - let parent handle overall placement
  const positionClasses = "relative w-[200px]";

  return (
    <div className={`bg-white rounded-lg shadow z-20 ${positionClasses}`} onClick={e => isInModal && e.stopPropagation()}>
      <form 
        ref={formRef} 
        className="flex flex-col space-y-4 px-5 py-2"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }}
        noValidate 
        autoComplete="off"
      >
        <div className="flex justify-between items-center">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Search
          </label>
          <MdRefresh
            size={20}
            className="text-gray-500 cursor-pointer text-yellow-500"
            onClick={(e) => {
              e.stopPropagation();
              resetFilters();
            }}
          />
        </div>
        <div className="flex items-center border border-gray-200 rounded-lg px-3 py-2 w-full text-[13px]">
          <FaSearch className="text-gray-500 mr-2 flex-shrink-0" />
          <input
            type="text"
            placeholder="Search files..."
            className="flex-grow outline-none overflow-hidden text-ellipsis whitespace-nowrap"
            style={{ minWidth: 0 }}
            value={filterTitle}
            onChange={(e) => setFilterTitle(e.target.value)}
            onKeyDown={handleEnterKey}
            onClick={e => isInModal && e.stopPropagation()}
            autoComplete="off"
          />
          {filterTitle && (
            <FaTimes
              className="text-gray-500 ml-2 cursor-pointer flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                setFilterTitle('');
              }}
            />
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Media Type</label>
          <select
            className="border border-gray-200 rounded-lg px-3 py-2 w-full text-[13px]"
            value={filterMediaType}
            onChange={(e) => setFilterMediaType(e.target.value)}
            onClick={e => isInModal && e.stopPropagation()}
          >
            <option>All Files</option>
            <option>Images</option>
            <option>Videos</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Time Uploaded</label>
          <select
            className="border border-gray-200 rounded-lg px-3 py-2 w-full text-[13px]"
            value={filterTimeUploaded}
            onChange={(e) => setFilterTimeUploaded(e.target.value)}
            onClick={e => isInModal && e.stopPropagation()}
          >
            <option>All Time</option>
            <option>Last 24 hours</option>
            <option>Last 7 days</option>
            <option>Last 30 days</option>
          </select>
        </div>

        <Button 
          type="button" 
          onClick={handleSearchClick} 
          className="w-full"
        >
          Apply Filters
        </Button>
      </form>
    </div>
  );
};

export default FilterPanel; 